"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.readPageSubdocId = readPageSubdocId;
exports.classifyPageSubdocId = classifyPageSubdocId;
exports.pagePartSubdocOf = pagePartSubdocOf;
exports.serverDatabaseIdOf = serverDatabaseIdOf;
exports.pageDocIdOf = pageDocIdOf;
exports.listPageSubdocs = listPageSubdocs;
const database_document_identity_1 = require("./database-document-identity");
const database_shard_directory_1 = require("./database-shard-directory");
const page_document_maps_1 = require("./page-document-maps");
/**
 * Reads an id without restricting its workspace, for archive remapping and
 * excluding system documents of any workspace. Other consumers classify in
 * their actual workspace with classifyPageSubdocId.
 */
function readPageSubdocId(docId) {
    const part = (0, database_document_identity_1.parseServerPagePartDocId)(docId);
    if (part)
        return {
            workspaceId: part.workspaceId,
            subdoc: {
                docId,
                kind: 'page_part',
                pageId: part.pageId,
                partId: part.partId,
            },
        };
    const database = (0, database_document_identity_1.parseServerDatabaseDocId)(docId);
    if (database) {
        const identity = {
            docId,
            databaseId: database.databaseId,
            local: false,
        };
        let subdoc;
        switch (database.kind) {
            case 'root':
                subdoc = { ...identity, kind: 'database-root' };
                break;
            case 'row-shard':
                subdoc = {
                    ...identity,
                    kind: 'database-row-shard',
                    shardId: database.shardId,
                };
                break;
            case 'directory-part':
                subdoc = {
                    ...identity,
                    kind: 'database-directory-part',
                    partId: database.partId,
                };
                break;
        }
        return { workspaceId: database.workspaceId, subdoc };
    }
    const localRoot = (0, database_document_identity_1.parseLocalDatabaseRootDocId)(docId);
    if (localRoot)
        return {
            workspaceId: null,
            subdoc: {
                docId,
                kind: 'database-root',
                databaseId: localRoot.databaseId,
                local: true,
            },
        };
    const localShard = (0, database_document_identity_1.parseLocalDatabaseRowShardDocId)(docId);
    return localShard
        ? {
            workspaceId: null,
            subdoc: {
                docId,
                kind: 'database-row-shard',
                databaseId: localShard.databaseId,
                shardId: localShard.shardId,
                local: true,
            },
        }
        : null;
}
/** A page subdocument in this workspace, including its local database ids. */
function classifyPageSubdocId(docId, workspaceId) {
    const identity = readPageSubdocId(docId);
    return identity &&
        (identity.workspaceId === null || identity.workspaceId === workspaceId)
        ? identity.subdoc
        : null;
}
/** The page part document `docId` of workspace `workspaceId`, else null. */
function pagePartSubdocOf(docId, workspaceId) {
    const subdoc = classifyPageSubdocId(docId, workspaceId);
    return subdoc?.kind === 'page_part' ? subdoc : null;
}
/** The database of the server system document `docId` of `workspaceId`, else null. */
function serverDatabaseIdOf(docId, workspaceId) {
    const subdoc = classifyPageSubdocId(docId, workspaceId);
    return subdoc && subdoc.kind !== 'page_part' && !subdoc.local
        ? subdoc.databaseId
        : null;
}
/** The page of a document: the page of a page part, else the document. */
function pageDocIdOf(docId, workspaceId) {
    return pagePartSubdocOf(docId, workspaceId)?.pageId ?? docId;
}
function databaseIdOf({ blockId, block }) {
    const databaseId = block.get('prop:databaseId');
    return typeof databaseId === 'string' && databaseId ? databaseId : blockId;
}
/**
 * The documents of a page besides the page document, the one place that
 * knows their kinds: the root of each database on the page, the parts of its
 * row-shard directory and its row shards that hold rows. A new kind of page
 * document is one more step here. Sorted by document id; a database that
 * the page references twice is listed once, with its last reference.
 */
async function listPageSubdocs(workspaceId, pageId, source) {
    const warnings = [];
    const subdocs = new Map();
    const databases = new Map();
    const add = (docId, kind, databaseId) => {
        const subdoc = classifyPageSubdocId(docId, workspaceId);
        if (!subdoc ||
            subdoc.kind === 'page_part' ||
            subdoc.kind !== kind ||
            subdoc.databaseId !== databaseId)
            throw new Error(`Invalid page subdocument ${docId}: expected ${kind} of database ${databaseId} in workspace ${workspaceId}`);
        subdocs.set(docId, subdoc);
    };
    for (const partId of (await source.pagePartIds(pageId)) ?? []) {
        if (partId === page_document_maps_1.PAGE_DOCUMENT_PART_ID)
            continue;
        const docId = (0, database_document_identity_1.createServerPagePartDocId)({ workspaceId, pageId, partId });
        subdocs.set(docId, { docId, kind: 'page_part', pageId, partId });
    }
    for (const reference of (await source.databaseBlocks(pageId)) ?? []) {
        const databaseId = databaseIdOf(reference);
        const rootDocId = reference.block.get('prop:databaseRootDocId');
        if (typeof rootDocId !== 'string' || !rootDocId)
            continue;
        const root = classifyPageSubdocId(rootDocId, workspaceId);
        if (root?.kind !== 'database-root' ||
            root.databaseId !== databaseId ||
            rootDocId !== root.docId) {
            warnings.push(`Ignoring invalid database root reference: workspace=${workspaceId}, ` +
                `pageDoc=${pageId}, database=${databaseId}, rootDoc=${rootDocId}`);
            continue;
        }
        const rootBlocks = await source.databaseBlocks(rootDocId);
        if (!rootBlocks)
            continue;
        add(rootDocId, 'database-root', databaseId);
        let directoryFound = false;
        for (const rootBlock of rootBlocks) {
            if (databaseIdOf(rootBlock) !== databaseId)
                continue;
            for (const partDocId of (0, database_shard_directory_1.readDatabaseShardDirectoryBlockPartDocIds)(rootBlock.block)) {
                add(partDocId, 'database-directory-part', databaseId);
            }
            const directory = await source.readDirectory(rootDocId, rootBlock.block, databaseId);
            if (!directory)
                continue;
            if (directory.manifest.databaseRootDocId !== rootDocId) {
                warnings.push(`Database ${databaseId} root manifest points to ${directory.manifest.databaseRootDocId}, expected ${rootDocId}`);
                continue;
            }
            directoryFound = true;
            databases.set(databaseId, {
                databaseId,
                blockId: reference.blockId,
                rootDocId,
                directory,
            });
            for (const shard of directory.manifest.shards) {
                if (shard.rowCount > 0) {
                    add(shard.serverDocId, 'database-row-shard', databaseId);
                }
            }
        }
        if (!directoryFound) {
            warnings.push(`Database ${databaseId} has no valid shard manifest in root ${rootDocId}`);
        }
    }
    return {
        subdocs: [...subdocs.values()].sort((a, b) => a.docId.localeCompare(b.docId)),
        databases: [...databases.values()].sort((a, b) => a.databaseId.localeCompare(b.databaseId)),
        warnings,
    };
}
